import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatScheduled } from "../utils/dateFormat";

interface Booking {
  bookingId: string;
  clientId: string;
  proId: string;
  proName: string;
  proCategory?: string;
  proPhoto?: string;
  serviceType: string;
  scheduledDate: string;
  address: string;
  notes?: string;
  jobSize?: string;
  category?: string;
  status: string;
  createdAt: string;
  quoteAmount?: number;
}

export default function BookingConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const booking = (location.state as { booking?: Booking } | null)?.booking;

  // Refresh / deep link → no state → send them to bookings list
  useEffect(() => {
    if (!booking) navigate("/bookings", { replace: true });
  }, [booking, navigate]);

  if (!booking) return null;

  const isPending = booking.status === "pending" || booking.status === "dispatched";
  const firstName = booking.proName.split(" ")[0];

  function buildIcsUrl(booking: Booking) {
    const start = new Date(booking.scheduledDate);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `UID:${booking.bookingId}@mounahub.com`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${booking.serviceType} with ${booking.proName}`,
      `LOCATION:${booking.address}`,
      `DESCRIPTION:Booking ID ${booking.bookingId} via Mouna`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Hero */}
      <div className="bg-gradient-to-br from-mouna-blue via-blue-600 to-navy px-5 pt-12 pb-16 text-white text-center relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-teal/10 rounded-full blur-2xl" />

        <div className="relative mx-auto w-24 h-24 mb-5">
          <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
          <div className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L20 7" stroke="#0E8C6A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold mb-2">
          {isPending ? "Request sent!" : "You're booked!"}
        </h1>
        <p className="text-sm text-white/80 max-w-xs mx-auto">
          {isPending
            ? `${firstName} typically responds within 1 hour. We'll notify you when they accept.`
            : `${firstName} has confirmed your appointment.`}
        </p>
      </div>

      {/* Summary card (overlaps hero) */}
      <div className="px-5 -mt-10 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-5">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            {booking.proPhoto ? (
              <img src={booking.proPhoto} alt={booking.proName} className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-blue-pale flex items-center justify-center text-mouna-blue font-bold text-lg">
                {booking.proName.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-navy truncate">{booking.proName}</div>
              <div className="text-xs text-gray-400">{booking.proCategory || booking.category || ""}</div>
            </div>
            <span className={"text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap " +
              (isPending ? "bg-yellow-50 text-yellow-700" : "bg-teal-light text-teal")}>
              {isPending ? "Pending" : "Confirmed"}
            </span>
          </div>

          {booking.quoteAmount && booking.quoteAmount > 0 ? (
            <div className="flex items-center justify-between p-3 bg-teal-light rounded-xl mt-4">
              <div>
                <div className="text-xs text-gray-500">Pro's quote</div>
                <div className="text-2xl font-display font-bold text-teal">${booking.quoteAmount}</div>
              </div>
              <div className="text-3xl">💵</div>
            </div>
          ) : null}

          <div className="pt-4 space-y-3 text-sm">
            <Row icon="🛠️" label="Service" value={booking.serviceType} />
            <Row icon="📅" label="When" value={formatScheduled(booking.scheduledDate)} />
            <Row icon="📍" label="Where" value={booking.address} />
            {booking.jobSize && <Row icon="📦" label="Job size" value={booking.jobSize} />}
            {booking.notes && <Row icon="📝" label="Notes" value={booking.notes} />}
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="px-5 mt-6">
        <h2 className="font-display text-base font-bold text-navy mb-3">What happens next</h2>
        <div className="bg-white rounded-2xl p-4 space-y-3">
          <Step n={1} active={true} title="Request sent" body={`${firstName} has been notified.`} />
          <Step n={2} active={!isPending} title="Pro confirms" body="You'll get a notification when they accept." />
          <Step n={3} active={false} title="Service day" body="Pro arrives at the scheduled time. You can message them anytime." />
        </div>
      </div>

      {/* CTAs */}
      <div className="px-5 mt-6 space-y-3">
        <button
          onClick={() =>
            navigate(
              `/messages?conversation=${booking.clientId}_${booking.proId}&recipient=${booking.proId}&name=${encodeURIComponent(booking.proName)}`
            )
          }
          className="w-full bg-mouna-blue text-white font-bold py-4 rounded-2xl text-sm shadow-lg shadow-mouna-blue/30"
        >
          💬 Message {firstName}
        </button>

        
        <a
          href={buildIcsUrl(booking)}
          download={`mouna-${booking.bookingId.slice(0, 8)}.ics`}
          className="block w-full bg-white border border-gray-200 text-navy font-bold py-4 rounded-2xl text-sm text-center"
        >
          📅 Add to calendar
        </a>

        <button onClick={() => navigate("/bookings")} className="w-full bg-gray-100 text-navy font-bold py-4 rounded-2xl text-sm">
          View my bookings
        </button>
      </div>

      {/* Footer */}
      <div className="px-5 mt-8 text-center">
        <p className="text-xs text-gray-400">
          Booking ID: <span className="font-mono">{booking.bookingId.slice(0, 8).toUpperCase()}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Need help?{" "}
          <button onClick={() => navigate("/support")} className="text-mouna-blue font-semibold underline">
            Contact support
          </button>
        </p>
      </div>
    </div>
  );
}

function Row({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-sm flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-400 mb-0.5">{label}</div>
        <div className="text-sm text-navy font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

function Step({ n, active, title, body }: { n: number; active: boolean; title: string; body: string }) {
  return (
    <div className="flex gap-3">
      <div className={"w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 " +
        (active ? "bg-teal text-white" : "bg-gray-100 text-gray-400")}>
        {active ? "✓" : n}
      </div>
      <div className="flex-1">
        <div className={"text-sm font-bold " + (active ? "text-navy" : "text-gray-400")}>{title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{body}</div>
      </div>
    </div>
  );
}
